import clsx from 'clsx'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import $type from '@tandem/ui/type.css'
import $form from '@tandem/ui/form.css'
import $badge from '@tandem/ui/badge.css'
import Spinner from '@tandem/ui/Spinner'
import Modal from '@tandem/ui/Modal'
import $modal from '@tandem/ui/Modal/styles.css'
import $table from '@tandem/ui/table.css'

import { useTRPC, type RouterOutputs } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import { memberRoleLabels } from '@tandem/schema/member'
import Dropdown from '@tandem/ui/Dropdown'
import $dropdownSelect from '@tandem/ui/DropdownSelect/styles.css'
import EllipsisIcon from '@tandem/ui/icons/ellipsis.svg?react'
import { UserPlusIcon, PencilIcon as Edit2Icon, OctagonXIcon as XOctagonIcon } from 'lucide-react'
import AddMemberForm from '@/staff/AddMemberForm'
import EditMemberForm from '@/staff/EditMemberForm'
import Accounts from '@/staff/Accounts'
import $ from './styles.css'

type Member = RouterOutputs['member']['list'][number]

export const Route = createFileRoute('/staff/')({
  component: StaffPage
})

function StaffPage() {
  let [showModal, setShowModal] = useState(false)
  let [activeMember, setActiveMember] = useState<Member | null>(null)

  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let { company } = useAuth()
  let companyId = company!.company_id

  let { data: members = [], isFetching } = useQuery(trpc.member.list.queryOptions({ company_id: companyId }))

  let removal = useMutation(
    trpc.member.remove.mutationOptions({
      onSuccess() {
        queryClient.invalidateQueries(trpc.member.list.queryFilter())
      }
    })
  )

  let closeModal = useCallback(() => {
    setShowModal(false)
    setActiveMember(null)
  }, [])

  return (
    <div class={clsx('container', $.container)}>
      <h3>Сотрудники</h3>
      <header class={$.header}>
        <div class={$.search}>
          <input type='text' placeholder='Найти' />
        </div>
        <div class={$.controls}>
          <button
            class={$.create}
            onClick={() => {
              setActiveMember(null)
              setShowModal(true)
            }}
          >
            <UserPlusIcon />
            Добавить сотрудника
          </button>
        </div>
      </header>

      {members.length > 0 ?
        <table class={clsx('unstyled', $table.table)}>
          <thead>
            <tr>
              <th>Имя</th>
              <th>Роль</th>
              <th>Эл. почта</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr key={member.user_id}>
                <td class={$.name}>
                  {member.last_name} {member.first_name}
                </td>
                <td>
                  <span class={$badge.pill}>
                    <span>{memberRoleLabels[member.role] ?? member.role}</span>
                  </span>
                </td>
                <td>{member.emails.map((e) => e.address).join(', ')}</td>
                <td>
                  <Dropdown
                    trigger={
                      <button class={clsx('plain', $type.muted)} title='Действия'>
                        <EllipsisIcon style={{ width: '1em' }} />
                      </button>
                    }
                  >
                    <div class={$dropdownSelect.menu}>
                      <button
                        class={clsx('ghost', $dropdownSelect.option)}
                        onClick={() => {
                          setActiveMember(member)
                          setShowModal(true)
                        }}
                      >
                        <Edit2Icon />
                        Редактировать
                      </button>
                      <button
                        class={clsx('ghost', 'danger', $dropdownSelect.option)}
                        disabled={removal.isPending}
                        onClick={() => {
                          if (confirm(`Удалить сотрудника «${member.last_name} ${member.first_name}»?`)) {
                            removal.mutate({ company_id: companyId, user_id: member.user_id })
                          }
                        }}
                      >
                        {removal.isPending ?
                          <Spinner />
                        : <XOctagonIcon />}
                        Удалить
                      </button>
                    </div>
                  </Dropdown>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      : <p class={$type.muted}>
          {isFetching ?
            <Spinner />
          : 'Нет сотрудников.'}
        </p>
      }

      {isFetching && members.length > 0 && <Spinner />}

      {company!.role === 'admin' && company!.schemas.length > 0 && <Accounts companyId={companyId} />}

      <Modal open={showModal} onClose={closeModal}>
        <div class={$modal.header}>
          <h2>{activeMember ? `Редактировать сотрудника` : 'Добавить сотрудника'}</h2>
        </div>
        {activeMember ?
          <EditMemberForm
            member={activeMember}
            companyId={companyId}
            onCancel={closeModal}
            onSuccess={closeModal}
            class={clsx($form.modal, $form.fullWidth)}
          />
        : <AddMemberForm
            companyId={companyId}
            onCancel={closeModal}
            onSuccess={closeModal}
            class={clsx($form.modal, $form.fullWidth)}
          />
        }
      </Modal>
    </div>
  )
}
